import { T, useAddressAutocomplete } from '@agos/shared'
import { cn } from './cn'
import { Loader2, MapPin } from 'lucide-react'
import { useState, type InputHTMLAttributes, type ReactNode } from 'react'

/**
 * A text field that suggests addresses.
 *
 * The suggestion list is chosen on `mouseDown`, not `click`, because the input's
 * `blur` fires first otherwise and closes the list out from under the pointer.
 * That is legacy's fix and it is the reason the handler looks unusual.
 *
 * Picking a suggestion shows its display name immediately and then replaces it
 * with the provider's formatted address once the details call returns — so the
 * field never sits empty while a request is in flight.
 */

export interface AddressFieldProps {
  value: string
  onChange: (value: string) => void
  /** Called with the resolved coordinates when a suggestion is picked. */
  onResolved?: (place: { lat: number; lon: number; formattedAddress: string }) => void
  /**
   * Called when a picked suggestion could **not** be resolved - §9.372, FB-661.
   *
   * The text has already changed to what was picked by the time this fires, and the details call is
   * what failed, so a caller holding coordinates is holding the **previous** address's. Anything
   * that reads a point - a zone, a fee, a driver's pin - has to be told, or the screen and the map
   * disagree with nothing looking wrong.
   *
   * Optional because a caller that keeps no point has nothing to clear: the delivery settings page
   * stores the depot as text and passes nothing. The picked address is handed over because the
   * order builder answers by re-guessing the zone from that name, which it cannot do without it.
   */
  onUnresolved?: (address: string) => void
  placeholder?: string
  /**
   * A visible label. Omit it where the field sits in a form that labels itself — the order builder's
   * customer panel is a grid of bare inputs, and adding a caption to one of them alone reads as a
   * mistake.
   */
  label?: string
  /** The accessible name when no visible label is drawn. Falls back to `label`. */
  ariaLabel?: string
  /**
   * Passed through to the input. The order builder's e2e finds this field by
   * `[data-customer-address]`, and a component that swallowed it would break six assertions while
   * looking like an improvement.
   */
  inputProps?: InputHTMLAttributes<HTMLInputElement>
  /**
   * Drawn inside the input, against its right edge — §9.164. The checkout puts *Moja poloha* here
   * because the owner asked for it in the box rather than above it; the order builder passes
   * nothing and the field is unchanged.
   *
   * A slot and not a locate button: two screens share this component and only one of them wants
   * one. When it is set the input is padded on the right, so typed text cannot run underneath.
   */
  trailing?: ReactNode
  className?: string
}

/** Long enough that a two-letter town does not spam the metered provider. */
const CLOSE_DELAY_MS = 150

export function AddressField({
  value,
  onChange,
  onResolved,
  onUnresolved,
  placeholder,
  label,
  ariaLabel,
  inputProps,
  trailing,
  className,
}: AddressFieldProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const { suggestions, isLoading, failed, retry, clearSuggestions, fetchPlaceDetails } =
    useAddressAutocomplete(query)

  const pick = async (suggestion: { displayName: string; placeId: string }) => {
    onChange(suggestion.displayName)
    setQuery('')
    clearSuggestions()
    setIsOpen(false)

    const details = await fetchPlaceDetails(suggestion.placeId)
    if (!details) {
      // §9.372: the text changed two lines up and the coordinates did not. Whoever holds a point
      // holds the *previous* address's, so silence here is the wrong-address bug rather than a
      // no-op. `fetchPlaceDetails` answers null for every failure alike - a 502, a dropped
      // connection, and §10A.160's refusal of a Google place id after the geocoder was switched
      // to the local providers.
      onUnresolved?.(suggestion.displayName)
      return
    }
    if (details.formattedAddress) onChange(details.formattedAddress)
    onResolved?.(details)
  }

  return (
    <div className={className}>
      {label !== undefined && (
        <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="address-field">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id="address-field"
          type="text"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
            setQuery(event.target.value)
            setIsOpen(true)
          }}
          onFocus={() => {
            if (value.length >= 2) setIsOpen(true)
          }}
          // Late enough for a mouseDown on the list to win the race.
          onBlur={() => setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)}
          placeholder={placeholder}
          aria-label={ariaLabel ?? label}
          autoComplete="off"
          {...inputProps}
          className={cn(
            'w-full px-3 py-2.5 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all',
            // Room for whatever sits in the slot. Without it the caret walks under the button and
            // a long street name is read half-covered.
            trailing !== undefined && 'pr-12',
          )}
        />
        {trailing !== undefined && (
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center">
            {trailing}
          </div>
        )}

        {isOpen && (suggestions.length > 0 || isLoading || failed) && (
          <div className="absolute top-full left-0 mt-1 z-50 w-full bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {/* §9.790 - a search that failed says so; an empty list here read as *no such address*. */}
            {failed && !isLoading && (
              <div
                role="alert"
                data-address-search-failed
                className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-muted-foreground"
              >
                <span>
                  <T>Vyhľadávanie adries teraz neodpovedá.</T>
                </span>
                <button
                  type="button"
                  data-address-search-retry
                  // mouseDown, as the suggestions: blur would close the list before a click lands.
                  onMouseDown={(event) => {
                    event.preventDefault()
                    retry()
                  }}
                  className="shrink-0 text-xs font-semibold text-primary hover:underline"
                >
                  <T>Skúsiť znova</T>
                </button>
              </div>
            )}
            {isLoading && suggestions.length === 0 && (
              <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                <T>Vyhľadávam adresy...</T>
              </div>
            )}
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.placeId}
                type="button"
                // §10A.121 - a handle, because there was none. These were bare buttons with no role
                // and no attribute, so the only way to reach a suggestion from a test was by its
                // text - which is franchise data. No spec drove this field at all, and the owner's
                // *"I still could not use Záväzne objednať"* is what that cost: the whole delivery
                // path depended on a control the suite could not click.
                data-address-suggestion={suggestion.placeId}
                // mouseDown, not click: blur would close the list first.
                onMouseDown={(event) => {
                  event.preventDefault()
                  void pick(suggestion)
                }}
                className={cn(
                  'w-full text-left text-sm px-3 py-2 hover:bg-accent transition-colors',
                  'flex items-start gap-2',
                )}
              >
                <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <span className="line-clamp-2">{suggestion.displayName}</span>
              </button>
            ))}
            {/* §9.789 - the state's address register is CC BY 4.0, so a list showing its addresses names it.
                Only then: a credit under Photon's answers would name a source that was not used. */}
            {suggestions.some((suggestion) => suggestion.source === 'register') && (
              <p
                data-address-attribution
                className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border"
              >
                <T>Adresy: Register adries MV SR (CC BY 4.0)</T>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
